<template>
  <div class="gallery-view-container">
    <PremiumPage
      :title="gallery.title || 'Album Detail'"
      subtitle="View and manage images and videos in this album"
      showBack
    >
      <template #header-actions>
        <div class="header-controls">
          <!-- Hidden Input for Media -->
          <input
            type="file"
            multiple
            id="MediaUploadInput"
            accept="image/*,video/mp4"
            hidden
            @change="saveMedia"
          />

          <v-btn
            depressed
            class="btn-premium-primary"
            @click="triggerUpload"
            :loading="uploadLoading"
            small
          >
            <v-icon small>mdi-plus-circle-outline</v-icon>
            <span class="btn-label">Add Media</span>
          </v-btn>
        </div>
      </template>

      <div class="pa-4 pa-sm-4">
        <!-- ── Modern Tabs Structure ── -->
        <div class="premium-tabs-wrap mb-4">
          <div
            v-for="tab in tabs"
            :key="tab.name"
            class="premium-tab-item"
            :class="{ active: currentTab === tab.name }"
            @click="selectTab(tab.name)"
          >
            <v-icon
              size="18"
              class="mr-2"
              :color="currentTab === tab.name ? 'var(--brand)' : 'var(--tx3)'"
            >
              {{ tab.icon }}
            </v-icon>
            <span>{{ tab.label }}</span>
          </div>
        </div>

        <!-- ── Content Area ── -->
        <div class="tab-content-area">
          <transition name="fade" mode="out-in">
            <component
              :is="currentTabComponent"
              :key="currentTab"
              :loading="loading"
            />
          </transition>
        </div>
      </div>
    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import ImageGallery from "./ImageGallery.vue";
import VideoGallery from "./VideoGallery.vue";
import PremiumPage from "../../common/PremiumPage.vue";

export default {
  name: "ViewGallery",
  components: { PremiumPage, ImageGallery, VideoGallery },
  data() {
    return {
      loading: false,
      uploadLoading: false,
      gallery: {
        id: null,
        title: "",
      },
      tabs: [
        {
          name: "Image",
          label: "Photos",
          icon: "mdi-image-outline",
          component: "ImageGallery",
        },
        {
          name: "Video",
          label: "Videos",
          icon: "mdi-video-outline",
          component: "VideoGallery",
        },
      ],
      currentTab: "Image",
    };
  },
  computed: {
    currentTabComponent() {
      const tab = this.tabs.find((t) => t.name === this.currentTab);
      return tab ? tab.component : "ImageGallery";
    },
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    ...mapMutations("society", ["setSelectedTab"]),

    selectTab(name) {
      this.currentTab = name;
      this.storeSelectedTabValue();
    },
    storeSelectedTabValue() {
      this.setSelectedTab({ selectedTab: this.currentTab });
      localStorage.setItem("gallerySelectedTab", this.currentTab);
    },
    getGalleryDetail() {
      this.loading = true;
      this.$axios
        .get("societyadmin/gallery/" + this.$route.params.id)
        .then((response) => {
          this.loading = false;
          if (response.data.status) {
            this.gallery = response.data.gallery;
          } else {
            this.showErr(response.data.message);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    triggerUpload() {
      document.getElementById("MediaUploadInput").click();
    },
    saveMedia(e) {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      const allowedExtensions = ["jpg","jpeg","png","gif","pdf","svg","mp4",];
      const maxSize = 25 * 1024 * 1024; // 25MB


     for (let i = 0; i < files.length; i++) {
  const extension = files[i].name.split(".").pop().toLowerCase();

  if (!allowedExtensions.includes(extension)) {
    this.showErr(
      `Invalid file extension ${extension}. Only jpg, png, gif, pdf, jpeg, svg, mp4 are allowed`
    );

    e.target.value = "";
    return;
  }

  if (files[i].size > maxSize) {
    this.showErr(
      `File size exceeds 25 MB. Please upload a smaller file`
    );

    e.target.value = "";
    return;
  }
}


      this.uploadLoading = true;
      let formData = new FormData();
      formData.append("title", this.gallery.title);
      for (let i = 0; i < files.length; i++) {
        formData.append("documents[]", files[i]);
      }

      this.$axios
        .put("/societyadmin/gallery/" + this.gallery.id, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          this.uploadLoading = false;
          if (response.data.status) {
            this.setAlert({
              showAlert: true,
              message: "Media uploaded successfully",
              variant: "green accent-4",
              timeout: 2000,
            });
            // Force children components to refresh by notifying them or re-loading
            this.loading = true; // Briefly toggle to trigger watcher in children
            this.$nextTick(() => {
              this.loading = false;
            });
          } else {
             let message = response.data.message || "Something went wrong";

            if (
              response.data.errors &&
              response.data.errors.length > 0
            ) {
              message = response.data.errors[0].message;
            }

            this.showErr(message);
          }
        })
        .catch(() => {
          this.uploadLoading = false;
        });
    },
    showErr(msg) {
      this.setAlert({
        showAlert: true,
        message: msg,
        variant: "red accent-3",
        timeout: 2000,
      });
    },
  },
  mounted() {
    const savedTab = localStorage.getItem("gallerySelectedTab");
    if (savedTab) this.currentTab = savedTab;
    this.getGalleryDetail();
  },
};
</script>

<style scoped>
.gallery-view-container {
  padding: 24px;
}

.add-media-btn {
  border-radius: 12px;
  font-weight: 700;
  text-transform: none;
  height: 42px !important;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.btn-label {
  white-space: nowrap;
}

/* ── Custom Premium Tabs ── */
.premium-tabs-wrap {
  display: flex;
  gap: 8px;
  height: auto;
  background: var(--surface2);
  padding: 6px;
  border-radius: 8px !important;
  width: fit-content;
  border: 1px solid var(--border);
}

.premium-tab-item {
  display: flex;
  align-items: center;
  padding: 10px 24px;
  border-radius: 8px !important;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: var(--tx2);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
}

.premium-tab-item:hover {
  color: var(--brand);
  background: rgba(16, 185, 129, 0.05);
}

.premium-tab-item.active {
  background: var(--surface, #ffffff);
  color: var(--brand);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

/* ── Content Transitions ── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .gallery-view-container {
    padding: 8px;
  }
  .premium-tabs-wrap {
    width: 100%;
  }
  .premium-tab-item {
    padding: 8px 8px;
    flex: 1;
    justify-content: center;
  }
  .btn-label {
    display: none;
  }
}

/* ── Dark mode ── */
.theme--dark .premium-tabs-wrap {
  background: var(--surface2);
  border-color: var(--border);
}

.theme--dark .premium-tab-item {
  color: var(--tx3);
}

.theme--dark .premium-tab-item:hover {
  background: var(--brand-lt);
  color: var(--brand);
}

.theme--dark .premium-tab-item.active {
  background: var(--surface3, #262626);
  color: var(--brand);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
</style>
